import { Loading } from "../../app/Status";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { BuildingSummary, ClientDetailDto, ClientDto, MemberClientAccess, MemberRow } from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useBuildings } from "../../lib/buildingContext";
import { useClients } from "../../lib/clientContext";
import { useToast } from "../../lib/toast";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Korte omschrijving van de toegang van een lid, voor de ledenlijst. */
export function accessSummary(access: MemberClientAccess[], clients: ClientDto[] | undefined): string {
  if (!access.length) return "geen";
  return access
    .map((a) => {
      const name = clients?.find((c) => c.id === a.clientId)?.name ?? "klant";
      if (a.scope === "all_buildings") return `${name} (alle gebouwen)`;
      const parts = [
        a.siteIds.length ? plural(a.siteIds.length, "locatie", "locaties") : "",
        a.buildingIds.length ? plural(a.buildingIds.length, "gebouw", "gebouwen") : "",
      ].filter(Boolean);
      return `${name} (${parts.join(", ") || "niets gekozen"})`;
    })
    .join(", ");
}

/**
 * Toegang van een medewerker per klant (docs/CLIENTS.md §7.5): alle gebouwen van een klant (ook nieuwe), of een
 * selectie van locaties (met hun toekomstige gebouwen) en losse gebouwen.
 */
export function AccessDialog({
  member,
  onClose,
  onSaved,
}: {
  member: MemberRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const clients = useClients();
  const buildings = useBuildings();
  const [access, setAccess] = useState<Map<string, MemberClientAccess>>(
    () => new Map(member.access.map((a) => [a.clientId, a])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = member.name ?? member.email;

  const update = (clientId: string, next: MemberClientAccess | null) =>
    setAccess((cur) => {
      const m = new Map(cur);
      if (next) m.set(clientId, next);
      else m.delete(clientId);
      return m;
    });

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api(`/org/members/${member.userId}/access`, { method: "PUT", body: { clients: [...access.values()] } });
      toast(`Toegang van ${who} bijgewerkt`);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Opslaan mislukt");
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Toegang · ${who}`}
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
            Opslaan
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <p className="hint">
        Kies per klant: alle gebouwen (ook die er later bij komen), of een selectie. Een locatie geeft al haar gebouwen,
        ook nieuwe.
      </p>
      {clients.isPending && <Loading />}
      <div className="accesslist">
        {clients.data?.map((c) => (
          <ClientAccess
            key={c.id}
            client={c}
            buildings={(buildings.data ?? []).filter((b) => b.clientId === c.id)}
            value={access.get(c.id) ?? null}
            onChange={(next) => update(c.id, next)}
          />
        ))}
      </div>
    </Modal>
  );
}

function ClientAccess({
  client,
  buildings,
  value,
  onChange,
}: {
  client: ClientDto;
  buildings: BuildingSummary[];
  value: MemberClientAccess | null;
  onChange: (v: MemberClientAccess | null) => void;
}) {
  const selected = value?.scope === "selected";
  const detail = useQuery({
    queryKey: ["client", client.id],
    queryFn: () => api<ClientDetailDto>(`/clients/${client.id}`),
    enabled: selected,
  });
  const sites = detail.data?.sites ?? [];
  const toggle = (list: string[], id: string, on: boolean) => (on ? [...list, id] : list.filter((x) => x !== id));
  const buildingBox = (b: BuildingSummary, viaSite: boolean) => (
    <label key={b.id} className="accessitem">
      <input
        type="checkbox"
        checked={viaSite || value!.buildingIds.includes(b.id)}
        disabled={viaSite}
        onChange={(e) => onChange({ ...value!, buildingIds: toggle(value!.buildingIds, b.id, e.target.checked) })}
      />
      <span>
        <span className="code">{b.code}</span> {b.name}
      </span>
    </label>
  );

  return (
    <fieldset className="accessclient">
      <legend>
        <label>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) =>
              onChange(
                e.target.checked ? { clientId: client.id, scope: "all_buildings", siteIds: [], buildingIds: [] } : null,
              )
            }
          />{" "}
          <strong>{client.name}</strong>{" "}
          <span className="muted small">({plural(client.buildingCount, "gebouw", "gebouwen")})</span>
        </label>
      </legend>
      {value && (
        <div className="accessbody">
          <div className="segrow" role="radiogroup" aria-label={`Toegang tot ${client.name}`}>
            <label>
              <input
                type="radio"
                name={`scope-${client.id}`}
                checked={!selected}
                onChange={() => onChange({ ...value, scope: "all_buildings" })}
              />{" "}
              Alle gebouwen
            </label>
            <label>
              <input
                type="radio"
                name={`scope-${client.id}`}
                checked={selected}
                onChange={() => onChange({ ...value, scope: "selected" })}
              />{" "}
              Selectie
            </label>
          </div>
          {selected && detail.isPending && <Loading small />}
          {selected && detail.data && (
            <div className="accesstree">
              {sites.map((s) => {
                const on = value.siteIds.includes(s.id);
                return (
                  <div key={s.id} className="accesssite">
                    <label className="accessitem">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => onChange({ ...value, siteIds: toggle(value.siteIds, s.id, e.target.checked) })}
                      />
                      <span>
                        Locatie <strong>{s.name}</strong>{" "}
                        <span className="muted small">(alle gebouwen, ook nieuwe)</span>
                      </span>
                    </label>
                    <div className="accesschildren">
                      {buildings.filter((b) => b.siteId === s.id).map((b) => buildingBox(b, on))}
                    </div>
                  </div>
                );
              })}
              {buildings.filter((b) => !b.siteId).map((b) => buildingBox(b, false))}
              {!buildings.length && <p className="muted small">Deze klant heeft nog geen gebouwen.</p>}
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}
